/**
 * woSchedule.js: the ONE reader of a work order's start and stop (IDI-471,
 * IDI-475). Every surface that shows when a work order runs reads it here:
 * the job's Work Orders row, the header strip, Edit, and the calendar.
 *
 * Two stored shapes, both live (measured on tenant 1, 2026-10-14):
 *   net-new   scheduledDate "2026-09-29" + startTime "08:00"
 *             + endDate "2026-09-29" + endTime "17:00"      (7 of 8 WOs)
 *   migrated  startAt "2024-09-25T13:30:00Z" + stopAt "2024-09-25T21:00:00Z"
 *             (scheduledDate = startAt)                     (1 of 8)
 *
 * The bugs this replaces:
 *  - every reader built the STOP from the START date ("WOs on this tenant
 *    are single-day"), so an Oct 7 -> Oct 9 work order showed Oct 7 only;
 *  - the calendar parsed a bare "2026-10-07" with new Date(), which is UTC
 *    midnight = 7 PM the day before in US Central.
 *
 * Bare dates and HH:mm are LOCAL wall-clock. An ISO timestamp is an instant.
 * U-13 (Adrian 2026-10-09): Create/Edit save the stop as an absolute
 * timestamp with offset (`stopAt`); this module also reads the older pairs.
 */

import { formatDate, formatTime } from './formatDate';

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;
const HHMM = /^([01]?\d|2[0-3]):([0-5]\d)/;
const first = (wo, keys) => {
  for (const k of keys) {
    const v = wo?.[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
};

/** A local Date from "YYYY-MM-DD" (+ optional "HH:mm"), or from an ISO instant. */
export function localDate(dateStr, timeStr) {
  if (!dateStr) return null;
  const ymd = String(dateStr).slice(0, 10).match(YMD);
  const hasClock = String(dateStr).includes('T');
  if (hasClock && !timeStr) {
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (!ymd) return null;
  const t = timeStr ? String(timeStr).match(HHMM) : null;
  return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]), t ? Number(t[1]) : 0, t ? Number(t[2]) : 0);
}

/**
 * When a work order runs.
 * @returns {{
 *   start: Date|null, stop: Date|null,
 *   hasStartTime: boolean, hasStopTime: boolean,
 *   multiDay: boolean,
 *   startDate: string, startTime: string, stopDate: string, stopTime: string
 * }}  (the *Date / *Time strings are local "YYYY-MM-DD" / "HH:mm", for forms)
 */
export function woSchedule(wo) {
  // Start: an explicit instant beats a date; a time on the date beats midnight.
  const startAt = first(wo, ['startAt', 'start_at', 'scheduledStart']);
  const day = first(wo, ['scheduledDate', 'scheduled_date', 'startDate', 'start_date']);
  const startTime = first(wo, ['startTime', 'start_time', 'scheduledTime']);
  let start = null;
  let hasStartTime = false;
  if (startTime && day) {
    start = localDate(day.slice(0, 10), startTime);
    hasStartTime = true;
  } else if (startAt) {
    start = localDate(startAt);
    hasStartTime = true;
  } else if (day) {
    start = localDate(day);
    hasStartTime = day.includes('T');
  }

  // Stop: stopAt (U-13) / endAt, else endDate + endTime, else the start day + endTime.
  const stopAt = first(wo, ['stopAt', 'stop_at', 'endAt', 'end_at', 'scheduledEnd']);
  const endDay = first(wo, ['endDate', 'end_date', 'scheduledEndDate']);
  const endTime = first(wo, ['endTime', 'end_time']);
  let stop = null;
  let hasStopTime = false;
  if (stopAt) {
    stop = localDate(stopAt);
    hasStopTime = !!stop && stopAt.includes('T');
  } else if (endDay || endTime) {
    const base = endDay || (start ? ymdOf(start) : '');
    stop = localDate(base.slice(0, 10), endTime || undefined);
    hasStopTime = !!endTime;
  }
  if (start && stop && stop < start) stop = null; // never draw a backwards span

  return {
    start,
    stop,
    hasStartTime,
    hasStopTime,
    multiDay: !!(start && stop && ymdOf(stop) !== ymdOf(start)),
    startDate: start ? ymdOf(start) : '',
    startTime: start && hasStartTime ? hhmmOf(start) : '',
    stopDate: stop ? ymdOf(stop) : '',
    stopTime: stop && hasStopTime ? hhmmOf(stop) : '',
  };
}

function ymdOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function hhmmOf(d) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Every local calendar day the work order covers (start..stop inclusive). */
export function woDays(wo) {
  const s = woSchedule(wo);
  if (!s.start) return [];
  const out = [];
  const cur = new Date(s.start.getFullYear(), s.start.getMonth(), s.start.getDate());
  const last = s.stop ? new Date(s.stop.getFullYear(), s.stop.getMonth(), s.stop.getDate()) : new Date(cur); // IDI-613: a copy, not `cur` itself
  for (let i = 0; cur <= last && i < 62; i++) {
    out.push(ymdOf(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const fmtDay = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const fmtDayShort = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const fmtTime = (d) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

/**
 * "Oct 7, 2026 8:00 AM – Oct 9, 2026 12:00 PM", "Oct 7, 2026 8:00 AM – 5:00 PM",
 * "Oct 7, 2026", or '' when unscheduled.
 */
export function formatWoRange(wo) {
  const s = woSchedule(wo);
  if (!s.start) return '';
  const startTxt = s.hasStartTime ? `${fmtDay(s.start)} ${fmtTime(s.start)}` : fmtDay(s.start);
  if (!s.stop) return startTxt;
  if (!s.multiDay) return s.hasStopTime ? `${startTxt} \u2013 ${fmtTime(s.stop)}` : startTxt;
  const stopTxt = s.hasStopTime ? `${fmtDay(s.stop)} ${fmtTime(s.stop)}` : fmtDay(s.stop);
  return `${startTxt} \u2013 ${stopTxt}`;
}

/**
 * IDI-537: a calendar card's own range in the house format (utils/formatDate),
 * "10/16/2026 8:00 AM → 10/17/2026 12:00 PM". Reads the card's fields
 * (start/date + startTime, end + stopTime/endTime), so every day of a span
 * shows the same range. No stop → start only; no time → dates only.
 */
export function formatWoRangeHouse({ start, date, startTime, end, stopTime, endTime } = {}) {
  const a = localDate(start || date, startTime || undefined);
  if (!a) return '';
  const fmt = (d, timed) => (timed ? `${formatDate(d)} ${formatTime(d)}` : formatDate(d));
  const startTxt = fmt(a, !!startTime);
  const stopT = stopTime || endTime;
  const sameDay = !end || String(end).slice(0, 10) === String(start || date).slice(0, 10);
  if (!stopT && sameDay) return startTxt;
  const b = localDate(end || start || date, stopT || undefined);
  if (!b || b < a) return startTxt;
  return `${startTxt} \u2192 ${fmt(b, !!stopT)}`;
}

/** Short label for a calendar chip: "8:00 AM", or "Oct 7 8:00 AM – Oct 9 12:00 PM" for spans. */
export function formatWoChip(wo) {
  const s = woSchedule(wo);
  if (!s.start) return '';
  const t = s.hasStartTime ? fmtTime(s.start) : '';
  if (s.multiDay) {
    // IDI-537: a span names its stop time too.
    const stopTxt = `${fmtDayShort(s.stop)}${s.hasStopTime ? ` ${fmtTime(s.stop)}` : ''}`;
    return `${fmtDayShort(s.start)}${t ? ` ${t}` : ''} \u2013 ${stopTxt}`;
  }
  if (t && s.hasStopTime) return `${t} \u2013 ${fmtTime(s.stop)}`;
  return t;
}

/**
 * The fields Edit saves for a schedule (IDI-471 + U-13): the local pairs the
 * create sheet writes, plus `startAt`/`stopAt` as absolute instants so every
 * reader (and the server's auto-complete) agrees.
 */
export function scheduleWrite({ startDate, startTime, stopDate, stopTime }) {
  const out = {};
  if (startDate) {
    out.scheduledDate = startDate;
    out.startDate = startDate;
    out.startTime = startTime || '';
    out.startAt = localDate(startDate, startTime || undefined)?.toISOString() || '';
  }
  if (stopDate || stopTime) {
    const sd = stopDate || startDate;
    out.endDate = sd || '';
    out.endTime = stopTime || '';
    out.stopAt = sd ? (localDate(sd, stopTime || undefined)?.toISOString() || '') : '';
  }
  return out;
}

/** Refuse a stop before the start. Returns an error string or ''. */
export function scheduleError({ startDate, startTime, stopDate, stopTime }) {
  if (!startDate || !(stopDate || stopTime)) return '';
  const a = localDate(startDate, startTime || undefined);
  const b = localDate(stopDate || startDate, stopTime || undefined);
  if (a && b && b < a) return 'Stop must be after start.';
  return '';
}

const HM = (hhmm, dflt) => {
  const m = typeof hhmm === 'string' ? hhmm.match(HHMM) : null;
  return m ? Number(m[1]) + Number(m[2]) / 60 : dflt;
};

/**
 * IDI-475: the hours a calendar card fills on ONE local day. `days` is the
 * card's day list (woDays). A span runs from its start time on the first day,
 * all day in between, and until its stop time on the last day. A single-day
 * card uses its own start/end (end defaults to start + 1).
 */
export function cardHoursOnDay({ days, startTime, endTime, stopTime }, dayKey, fallbackStart = 9) {
  const start = HM(startTime, fallbackStart);
  if (!Array.isArray(days) || days.length < 2) {
    const end = HM(endTime, null);
    return { startH: start, endH: end != null ? end : start + 1 };
  }
  const first = dayKey === days[0];
  const last = dayKey === days[days.length - 1];
  return { startH: first ? HM(startTime, 0) : 0, endH: last ? HM(stopTime, 24) : 24 };
}

/** "YYYY-MM-DD" for a local Date (the key woDays uses). */
export function dayKey(d) {
  return ymdOf(d);
}

const fmtStamp = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};

/**
 * IDI-467 (Q-24): how a completed work order says it was completed, or ''.
 *   auto (IDI-466 derived)  "Completed automatically at <stop>"
 *   with the job (IDI-246)  "Completed with the job on <date> by <user>"
 *   anything else (legacy)  "Completed <date>"  (or just "Completed")
 */
export function woCompletionLabel(wo) {
  if (String(wo?.status || '').toLowerCase() !== 'completed') return '';
  const at = fmtStamp(wo?.completedAt);
  if (wo?.completedReason === 'auto') return at ? `Completed automatically at ${at}` : 'Completed automatically';
  if (wo?.completedReason === 'job') {
    const day = wo?.completedAt ? new Date(wo.completedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const by = wo?.completedBy ? ` by ${wo.completedBy}` : '';
    return `Completed with the job${day ? ` on ${day}` : ''}${by}`;
  }
  return at ? `Completed ${at}` : 'Completed';
}

/**
 * IDI-536: the WO's open time entry, read from its OWN time entries (the
 * first with a clock-in and no clock-out), then clockedInAt, exactly as the Go
 * reader's openEntry (cmd/jobs/auto_complete.go). The server's openTimeEntry
 * is used only when the WO carries no time-entry data at all, so Clock In and
 * Clock Out update the warning live, with no reload.
 */
export function woOpenEntry(wo) {
  const lists = [wo?.timeEntries, wo?.time_entries].filter(Array.isArray);
  for (const list of lists) {
    for (const e of list) {
      if (!e || typeof e !== 'object') continue;
      const clockIn = e.clockIn || e.clock_in;
      const clockOut = e.clockOut || e.clock_out;
      if (clockIn && !clockOut) return { id: e.id, clockIn, memberName: e.memberName || e.member_name || '' };
    }
  }
  const at = wo?.clockedInAt || wo?.clocked_in_at;
  if (at) return { clockIn: at, memberName: wo?.clockedInBy || wo?.clocked_in_by || '' };
  if (lists.length) return null;
  const srv = wo?.openTimeEntry;
  return srv && srv.clockIn ? srv : null;
}

/** IDI-467 (U-12/Q-25): the running-clock warning for a completed WO, or ''. */
export function woOpenClockWarning(wo) {
  if (String(wo?.status || '').toLowerCase() !== 'completed') return '';
  const open = woOpenEntry(wo);
  if (!open || !open.clockIn) return '';
  const who = open.memberName ? `${open.memberName}'s` : 'A';
  return `${who} clock is still running (since ${fmtStamp(open.clockIn)}). This work order is completed; clock out when the work is done.`;
}
